import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, Eye, EyeOff, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';

interface PasswordAnalysis {
  score: number;
  strength: 'Very Weak' | 'Weak' | 'Fair' | 'Good' | 'Strong' | 'Very Strong';
  feedback: string[];
  checks: {
    length: boolean;
    lowercase: boolean;
    uppercase: boolean;
    numbers: boolean;
    symbols: boolean;
    noCommon: boolean;
    noPersonal: boolean;
    noSequential: boolean;
  };
  estimatedCrackTime: string;
  entropy: number;
}

export const PasswordStrengthCheckerTool: React.FC = () => {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [analysis, setAnalysis] = useState<PasswordAnalysis | null>(null);

  const commonPasswords = [
    'password', '123456', '123456789', 'qwerty', 'abc123', 'password123',
    'admin', 'letmein', 'welcome', 'monkey', '1234567890', 'password1',
    'qwerty123', 'dragon', 'master', 'hello', 'login', 'welcome123'
  ];

  const calculateEntropy = (password: string): number => {
    let charsetSize = 0;
    if (/[a-z]/.test(password)) charsetSize += 26;
    if (/[A-Z]/.test(password)) charsetSize += 26;
    if (/[0-9]/.test(password)) charsetSize += 10;
    if (/[^a-zA-Z0-9]/.test(password)) charsetSize += 32;
    
    return Math.log2(Math.pow(charsetSize, password.length));
  };

  const estimateCrackTime = (entropy: number): string => {
    const guessesPerSecond = 1000000000; // 1 billion guesses per second
    const totalGuesses = Math.pow(2, entropy) / 2; // Average case
    const seconds = totalGuesses / guessesPerSecond;
    
    if (seconds < 1) return 'Instant';
    if (seconds < 60) return `${Math.round(seconds)} seconds`;
    if (seconds < 3600) return `${Math.round(seconds / 60)} minutes`;
    if (seconds < 86400) return `${Math.round(seconds / 3600)} hours`;
    if (seconds < 31536000) return `${Math.round(seconds / 86400)} days`;
    if (seconds < 31536000000) return `${Math.round(seconds / 31536000)} years`;
    return 'Centuries';
  };

  const hasSequentialChars = (password: string): boolean => {
    const sequences = [
      'abcdefghijklmnopqrstuvwxyz',
      'qwertyuiopasdfghjklzxcvbnm',
      '1234567890'
    ];
    
    const lowerPassword = password.toLowerCase();
    
    for (const sequence of sequences) {
      for (let i = 0; i <= sequence.length - 3; i++) {
        const subseq = sequence.substring(i, i + 3);
        if (lowerPassword.includes(subseq)) return true;
      }
    }
    
    return false;
  };

  const analyzePassword = (password: string): PasswordAnalysis => {
    const checks = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      numbers: /[0-9]/.test(password),
      symbols: /[^a-zA-Z0-9]/.test(password),
      noCommon: !commonPasswords.includes(password.toLowerCase()),
      noPersonal: true, // Would need personal info to check properly
      noSequential: !hasSequentialChars(password)
    };

    const entropy = calculateEntropy(password);
    const estimatedCrackTime = estimateCrackTime(entropy);

    // Calculate score based on checks
    let score = 0;
    if (checks.length) score += 20;
    if (checks.lowercase) score += 10;
    if (checks.uppercase) score += 10;
    if (checks.numbers) score += 10;
    if (checks.symbols) score += 15;
    if (checks.noCommon) score += 15;
    if (checks.noSequential) score += 10;
    if (password.length >= 12) score += 10;

    // Determine strength
    let strength: PasswordAnalysis['strength'];
    if (score < 30) strength = 'Very Weak';
    else if (score < 50) strength = 'Weak';
    else if (score < 70) strength = 'Fair';
    else if (score < 85) strength = 'Good';
    else if (score < 95) strength = 'Strong';
    else strength = 'Very Strong';

    // Generate feedback
    const feedback: string[] = [];
    if (!checks.length) feedback.push('Use at least 8 characters');
    if (!checks.lowercase) feedback.push('Add lowercase letters');
    if (!checks.uppercase) feedback.push('Add uppercase letters');
    if (!checks.numbers) feedback.push('Add numbers');
    if (!checks.symbols) feedback.push('Add special characters (!@#$%^&*)');
    if (!checks.noCommon) feedback.push('Avoid common passwords');
    if (!checks.noSequential) feedback.push('Avoid sequential characters');
    if (password.length < 12) feedback.push('Consider using 12+ characters for better security');

    if (feedback.length === 0) {
      feedback.push('Excellent! This is a strong password.');
    }

    return {
      score,
      strength,
      feedback,
      checks,
      estimatedCrackTime,
      entropy
    };
  };

  useEffect(() => {
    if (password) {
      setAnalysis(analyzePassword(password));
    } else {
      setAnalysis(null);
    }
  }, [password]);

  const getStrengthColor = (strength: string) => {
    switch (strength) {
      case 'Very Weak': return 'text-red-600 bg-red-100 dark:bg-red-900/20';
      case 'Weak': return 'text-red-500 bg-red-50 dark:bg-red-900/10';
      case 'Fair': return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20';
      case 'Good': return 'text-blue-600 bg-blue-100 dark:bg-blue-900/20';
      case 'Strong': return 'text-green-600 bg-green-100 dark:bg-green-900/20';
      case 'Very Strong': return 'text-green-700 bg-green-200 dark:bg-green-900/30';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-800';
    }
  };

  const getProgressColor = (score: number) => {
    if (score < 30) return 'bg-red-500';
    if (score < 50) return 'bg-red-400';
    if (score < 70) return 'bg-yellow-500';
    if (score < 85) return 'bg-blue-500';
    if (score < 95) return 'bg-green-500';
    return 'bg-green-600';
  };

  const CheckItem: React.FC<{ check: boolean; label: string }> = ({ check, label }) => (
    <div className="flex items-center space-x-2">
      {check ? (
        <CheckCircle className="w-4 h-4 text-green-600" />
      ) : (
        <XCircle className="w-4 h-4 text-red-500" />
      )}
      <span className={`text-sm ${check ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
        {label}
      </span>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Password Input */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Shield className="w-5 h-5" />
            <span>Password Strength Checker</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Enter Password to Analyze
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Type your password here..."
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                <Button
                  onClick={() => setShowPassword(!showPassword)}
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {analysis && (
              <div className="space-y-4">
                {/* Strength Indicator */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Password Strength</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStrengthColor(analysis.strength)}`}>
                      {analysis.strength}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(analysis.score)}`}
                      style={{ width: `${analysis.score}%` }}
                    />
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    Score: {analysis.score}/100
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {analysis && (
        <>
          {/* Security Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {Math.round(analysis.entropy)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Entropy (bits)</div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {password.length}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Characters</div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-lg font-bold text-purple-600 dark:text-purple-400">
                  {analysis.estimatedCrackTime}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Crack Time</div>
              </CardContent>
            </Card>
          </div>

          {/* Security Checks */}
          <Card>
            <CardHeader>
              <CardTitle>Security Requirements</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <CheckItem check={analysis.checks.length} label="At least 8 characters" />
                  <CheckItem check={analysis.checks.lowercase} label="Contains lowercase letters" />
                  <CheckItem check={analysis.checks.uppercase} label="Contains uppercase letters" />
                  <CheckItem check={analysis.checks.numbers} label="Contains numbers" />
                </div>
                <div className="space-y-3">
                  <CheckItem check={analysis.checks.symbols} label="Contains special characters" />
                  <CheckItem check={analysis.checks.noCommon} label="Not a common password" />
                  <CheckItem check={analysis.checks.noSequential} label="No sequential characters" />
                  <CheckItem check={password.length >= 12} label="12+ characters (recommended)" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Feedback */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <AlertTriangle className="w-5 h-5" />
                <span>Recommendations</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {analysis.feedback.map((item, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <span className="text-blue-500 mt-1">•</span>
                    <span className="text-sm text-gray-700 dark:text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </>
      )}

      {/* Security Tips */}
      <Card>
        <CardHeader>
          <CardTitle>Password Security Tips</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Creating Strong Passwords:</h4>
              <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                <li>• Use a mix of uppercase and lowercase letters</li>
                <li>• Include numbers and special characters</li>
                <li>• Make it at least 12 characters long</li>
                <li>• Avoid dictionary words and personal information</li>
                <li>• Don't use sequential characters (abc, 123)</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Best Practices:</h4>
              <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                <li>• Use a unique password for each account</li>
                <li>• Consider using a password manager</li>
                <li>• Enable two-factor authentication when available</li>
                <li>• Change passwords if you suspect compromise</li>
                <li>• Don't share passwords or write them down</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
